import React, { useState, useMemo, useEffect } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { rollDice, STANDARD_DICE } from '../../lib/dice';
import { equippedItem, quickAttackPlan, weaponMeta, isProficientWith } from '../../lib/equip';
import RollLog from './RollLog';

// One-stop dice tray. Used by both player view (pulls up from the bottom)
// and DM view (modal-ish panel). The component renders the picker / modifier
// / adv-dis / roll button + a scrollable log; the wrapper component decides
// where it floats and how it's revealed.
//
// When `playerRecord` is passed in, equipped weapons surface as one-tap
// "Quick attack" buttons at the top — rolls 1d20 + stat (+ prof if
// proficient) for to-hit, then weapon damage. Both rolls broadcast to the
// dice overlay so the table can see the action.

export default function DiceTray({ identity, onClose, playerRecord }) {
  const { submitDiceRoll, updatePlayer } = useCampaign();
  const [counts, setCounts] = useState({}); // { 4:0, 6:0, ..., 20:0, 100:0 }
  const [modifier, setModifier] = useState(0);
  const [mode, setMode] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  // DM-only: roll hidden from players + Display TV. Persists across rolls
  // (DM can do a streak of hidden checks without re-clicking) and resets
  // each time the tray is unmounted/remounted.
  const [hidden, setHidden] = useState(false);
  const isDm = !!identity?.isDm;

  // Inspiration: the player tapped "Use Inspiration" before opening the tray,
  // which flipped `inspirationPending` true on the player record. When set,
  // pre-fill 2d20 + advantage so the next roll just works. The flag clears
  // after the next d20-bearing roll commits. The player can still manually
  // edit dice / mode before rolling — we only force on the edge transition.
  const inspirationPending = !!playerRecord?.inspirationPending;
  const inspirationPlayerId = playerRecord?.id || identity?.playerId || null;
  useEffect(() => {
    if (!inspirationPending) return;
    setCounts((prev) => ({ ...prev, 20: Math.max(2, prev[20] || 0) }));
    setMode('advantage');
  }, [inspirationPending]);

  const total = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
  const has20 = (counts[20] || 0) > 0;
  const hasMultipleSameDie = Object.values(counts).some((count) => count >= 2);

  const bump = (sides, delta) => {
    setCounts((p) => {
      const next = Math.max(0, (p[sides] || 0) + delta);
      return { ...p, [sides]: next };
    });
  };

  const reset = () => {
    setCounts({});
    setModifier(0);
    setMode('normal');
  };

  // Equipped weapons (main + off-hand) → "Quick attack" buttons.
  const equippedWeapons = useMemo(() => {
    if (!playerRecord) return [];
    const inv = playerRecord.inventory || [];
    const main = equippedItem(inv, 'mainHand');
    const off  = equippedItem(inv, 'offHand');
    return [main, off].filter((it) => it && weaponMeta(it));
  }, [playerRecord]);

  // Two-step attack flow: roll to-hit first, prompt the player to declare
  // hit/miss, then roll damage only if they say it landed.
  // pendingAttack: { weapon, plan, d20, total }
  const [pendingAttack, setPendingAttack] = useState(null);

  const baseRollIdentity = () => ({
    playerId:   identity.playerId ?? null,
    playerName: identity.playerName,
    colour:     identity.colour,
    isDm:       !!identity.isDm,
  });

  const handleQuickAttack = async (weapon) => {
    if (!playerRecord || submitting || pendingAttack) return;
    setSubmitting(true);
    try {
      const plan = quickAttackPlan({ weaponItem: weapon, playerRecord });
      if (!plan) return;
      // If Inspiration is pending, bump the d20 to 2 and roll with advantage.
      const useInsp = inspirationPending;
      const toHitDice = useInsp
        ? plan.toHit.dice.map((d) => d.sides === 20 ? { ...d, count: Math.max(2, d.count || 1) } : d)
        : plan.toHit.dice;
      const toHit = rollDice({
        dice: toHitDice,
        modifier: plan.toHit.modifier,
        mode: useInsp ? 'advantage' : 'normal',
      });
      await submitDiceRoll({
        ...toHit,
        ...baseRollIdentity(),
        label: `${weapon.name} attack${useInsp ? ' ✨' : ''}`,
        ...(hidden ? { hidden: true } : {}),
      });
      if (useInsp && inspirationPlayerId) {
        updatePlayer(inspirationPlayerId, { inspirationPending: false });
      }
      const d20 = toHit.dice?.[0]?.kept?.[0] ?? null;
      setPendingAttack({ weapon, plan, d20, total: toHit.total });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmHit = async () => {
    if (!pendingAttack || submitting) return;
    setSubmitting(true);
    try {
      const { weapon, plan } = pendingAttack;
      const dmg = rollDice({ dice: plan.damage.dice, modifier: plan.damage.modifier, mode: 'normal' });
      await submitDiceRoll({
        ...dmg,
        ...baseRollIdentity(),
        label: `${weapon.name} damage`,
        ...(hidden ? { hidden: true } : {}),
      });
    } finally {
      setSubmitting(false);
      setPendingAttack(null);
    }
  };

  const declareMiss = () => {
    setPendingAttack(null);
  };

  const handleRoll = async () => {
    if (total === 0 || submitting) return;
    setSubmitting(true);
    try {
      const dice = STANDARD_DICE
        .map((sides) => ({ sides, count: counts[sides] || 0 }))
        .filter((d) => d.count > 0);
      const result = rollDice({ dice, modifier, mode });
      const usedInspiration = inspirationPending && (counts[20] || 0) >= 2;
      await submitDiceRoll({
        ...result,
        playerId:   identity.playerId ?? null,
        playerName: identity.playerName,
        colour:     identity.colour,
        isDm:       !!identity.isDm,
        ...(usedInspiration ? { label: '✨ Inspired roll' } : {}),
        ...(hidden ? { hidden: true } : {}),
      });
      // Clear the pending flag once the roll commits — even if the player
      // manually changed mode away from advantage, they "spent" the moment.
      if (inspirationPending && inspirationPlayerId) {
        updatePlayer(inspirationPlayerId, { inspirationPending: false });
      }
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.tray}>
      <div style={styles.header}>
        <span style={styles.title}>🎲 Dice</span>
        {onClose && (
          <button style={styles.closeBtn} onClick={onClose} title="Close">✕</button>
        )}
      </div>

      {equippedWeapons.length > 0 && !pendingAttack && (
        <div style={styles.quickAttackWrap}>
          <div style={styles.quickAttackLabel}>Quick attack</div>
          <div style={styles.quickAttackRow}>
            {equippedWeapons.map((w) => {
              const meta = weaponMeta(w);
              const profOK = isProficientWith(w, playerRecord?.weaponProficiencies);
              return (
                <button
                  key={w.id}
                  style={{ ...styles.quickAttackBtn, ...(submitting ? { opacity: 0.5 } : {}) }}
                  onClick={() => handleQuickAttack(w)}
                  disabled={submitting}
                  title={`Attack with ${w.name}: 1d20${profOK ? ' + prof' : ''} + ${meta?.stat?.toUpperCase() || 'STR'} for to-hit, then declare hit/miss → roll damage on hit`}
                >
                  <span style={styles.qaName}>{w.name}</span>
                  <span style={styles.qaMeta}>
                    {meta?.damageDie || 'd4'}
                    {profOK && <span style={styles.qaProf}> · prof</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {pendingAttack && (
        <div style={styles.hitPromptWrap}>
          <div style={styles.hitPromptHeader}>
            <span style={styles.hitPromptName}>{pendingAttack.weapon.name}</span>
            <span style={styles.hitPromptTotal}>
              rolled <strong>{pendingAttack.total}</strong>
              {pendingAttack.d20 != null && (
                <span style={styles.hitPromptD20}> (d20: {pendingAttack.d20})</span>
              )}
            </span>
          </div>
          <div style={styles.hitPromptBtns}>
            <button style={styles.missBtn} onClick={declareMiss} disabled={submitting}>
              ✕ Miss
            </button>
            <button style={styles.hitBtn} onClick={confirmHit} disabled={submitting}>
              ✓ Hit — roll damage
            </button>
          </div>
        </div>
      )}

      <div style={styles.diceGrid}>
        {STANDARD_DICE.map((sides) => {
          const c = counts[sides] || 0;
          return (
            <button
              key={sides}
              style={{ ...styles.die, ...(c > 0 ? styles.dieActive : {}) }}
              onClick={() => bump(sides, +1)}
              onContextMenu={(e) => { e.preventDefault(); bump(sides, -1); }}
              title={`d${sides} — tap to add, right-click / long-press to remove`}
            >
              <span style={styles.dieLabel}>d{sides}</span>
              {c > 0 && <span style={styles.dieCount}>{c}</span>}
            </button>
          );
        })}
      </div>

      <div style={styles.controls}>
        <div style={styles.modRow}>
          <span style={styles.modLabel}>Modifier</span>
          <button style={styles.stepBtn} onClick={() => setModifier((m) => m - 1)}>−</button>
          <span style={styles.modValue}>{modifier >= 0 ? `+${modifier}` : modifier}</span>
          <button style={styles.stepBtn} onClick={() => setModifier((m) => m + 1)}>+</button>
        </div>

        {inspirationPending && (
          <div style={styles.inspirationBadge}>
            ✨ Inspired — this roll uses advantage
          </div>
        )}

        {/* DM-only: hide the next rolls from players + Display TV. Toggle
            persists across rolls (DM can do a streak of secret checks). */}
        {isDm && (
          <button
            type="button"
            style={{ ...styles.hiddenToggle, ...(hidden ? styles.hiddenToggleActive : {}) }}
            onClick={() => setHidden((v) => !v)}
            title={hidden ? 'Hidden — only you see this roll' : 'Mark next roll(s) hidden from players'}
          >
            {hidden ? '🔒 Hidden — only you see this roll' : '🔓 Roll publicly'}
          </button>
        )}

        <div style={styles.advRow}>
          {[
            { id: 'disadvantage', label: 'Disadvantage', color: '#c03333' },
            { id: 'normal',       label: 'Normal',       color: '#6a7a8a' },
            { id: 'advantage',    label: 'Advantage',    color: '#5cb870' },
          ].map((opt) => {
            const active   = mode === opt.id;
            const enabled  = hasMultipleSameDie || opt.id === 'normal';
            return (
              <button
                key={opt.id}
                disabled={!enabled}
                onClick={() => setMode(opt.id)}
                style={{
                  ...styles.advBtn,
                  ...(active  ? { borderColor: opt.color, color: opt.color, background: `${opt.color}20` } : {}),
                  ...(!enabled ? { opacity: 0.35, cursor: 'not-allowed' } : {}),
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div style={styles.actionRow}>
          {total > 0 && (
            <button style={styles.resetBtn} onClick={reset} disabled={submitting}>
              Reset
            </button>
          )}
          <button
            style={{ ...styles.rollBtn, ...(total === 0 ? styles.rollBtnDisabled : {}) }}
            onClick={handleRoll}
            disabled={total === 0 || submitting}
          >
            {submitting ? 'Rolling…' : total === 0 ? 'Pick dice' : `🎲 Roll (${total})`}
          </button>
        </div>
      </div>

      <div style={styles.logHeader}>Recent Rolls</div>
      <RollLog maxHeight={220} viewerIdentity={identity} />
    </div>
  );
}

const styles = {
  tray: {
    display: 'flex', flexDirection: 'column',
    background: 'linear-gradient(180deg, #1a2a48, #142038)',
    border: '2px solid #8b7340',
    borderRadius: '10px 10px 0 0',
    width: '100%',
    maxWidth: 480,
    boxShadow: '0 -16px 40px rgba(0,0,0,0.7)',
    color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 14px',
    borderBottom: '1px solid #4a3d28',
    background: 'rgba(0,0,0,0.2)',
  },
  title:    { fontSize: 16, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn: { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 18, cursor: 'pointer', padding: '0 4px' },
  quickAttackWrap:  { padding: '8px 12px 0', display: 'flex', flexDirection: 'column', gap: 4 },
  quickAttackLabel: { fontSize: 9, color: '#9a8b6e', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' },
  quickAttackRow:   { display: 'flex', gap: 6, flexWrap: 'wrap' },
  quickAttackBtn:   { flex: '1 1 120px', minWidth: 110, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '8px 10px', background: 'rgba(212,168,71,0.12)', border: '1px solid rgba(212,168,71,0.5)', color: '#d4a847', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.02em' },
  qaName:           { fontSize: 12, fontWeight: 800 },
  qaMeta:           { fontSize: 10, color: '#9a8b6e', fontWeight: 700 },
  qaProf:           { color: '#86efac' },

  hitPromptWrap:    { display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', margin: '0 12px', marginTop: 8, background: 'linear-gradient(180deg, rgba(212,168,71,0.18), rgba(166,131,48,0.12))', border: '2px solid #d4a847', borderRadius: 6 },
  hitPromptHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 },
  hitPromptName:    { fontSize: 13, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },
  hitPromptTotal:   { fontSize: 12, color: '#f0e6d0', fontWeight: 700 },
  hitPromptD20:     { fontSize: 11, color: '#9a8b6e', fontWeight: 600, marginLeft: 4 },
  hitPromptBtns:    { display: 'flex', gap: 8 },
  hitBtn:           { flex: 1, padding: '10px 0', background: 'linear-gradient(180deg, #5cb870, #3a8a4a)', border: '1px solid #5cb870', color: '#0a1a10', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, boxShadow: '0 4px 14px rgba(74,222,128,0.3)', letterSpacing: '0.04em' },
  missBtn:          { flex: 1, padding: '10px 0', background: 'rgba(192,51,51,0.15)', border: '1px solid #c03333', color: '#f87171', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
  diceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 6,
    padding: 12,
  },
  die: {
    position: 'relative',
    aspectRatio: '1',
    minHeight: 48,
    borderRadius: 6,
    border: '1px solid #4a3d28',
    background: '#0e1528',
    color: '#9a8b6e',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Press Start 2P', monospace",
  },
  dieActive: { borderColor: '#d4a847', color: '#d4a847', background: '#1a2a48', boxShadow: '0 0 8px rgba(212,168,71,0.35)' },
  dieLabel:  { lineHeight: 1 },
  dieCount: {
    position: 'absolute', top: 2, right: 4,
    fontSize: 11, fontWeight: 800, color: '#1a0e00',
    background: '#facc15', borderRadius: '50%',
    minWidth: 18, height: 18, padding: '0 3px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  controls: { padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 8 },
  modRow:   { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
  modLabel: { fontSize: 12, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 },
  stepBtn:  { width: 36, height: 32, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', fontSize: 18, fontWeight: 800, cursor: 'pointer' },
  modValue: { width: 50, textAlign: 'center', fontWeight: 800, fontSize: 16, color: '#d4a847' },
  inspirationBadge: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', background: 'rgba(251,191,36,0.15)', border: '1px solid #fbbf24', borderRadius: 4, color: '#fde68a', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', fontFamily: "'Press Start 2P', monospace", boxShadow: '0 0 8px rgba(251,191,36,0.25)' },
  hiddenToggle:    { padding: '6px 10px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', transition: 'all 0.12s' },
  hiddenToggleActive: { background: 'rgba(167,139,250,0.15)', borderColor: '#a78bfa', color: '#ddd6fe', boxShadow: '0 0 10px rgba(167,139,250,0.35)' },
  advRow:   { display: 'flex', gap: 4 },
  advBtn:   { flex: 1, padding: '8px 4px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.02em' },
  actionRow:{ display: 'flex', gap: 8 },
  resetBtn: { padding: '10px 12px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  rollBtn:  {
    flex: 1, padding: '12px 0',
    background: 'linear-gradient(180deg, #d4a847, #a68330)',
    border: '1px solid #c4a44a',
    color: '#1a0e00',
    fontSize: 16, fontWeight: 800, fontFamily: "'Press Start 2P', monospace",
    cursor: 'pointer', borderRadius: 4,
    boxShadow: '0 4px 14px rgba(212,168,71,0.35)',
  },
  rollBtnDisabled: { background: '#1a2a48', color: '#4a3d28', border: '1px solid #2a3552', cursor: 'not-allowed', boxShadow: 'none' },
  logHeader: { padding: '8px 14px 0', fontSize: 11, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 },
};
